import { BookerByIdQuery, EnumProductType, Product } from "gql/generated";
import { i18nLanguage } from "i18n";
import { PropsWithChildren, useMemo } from "react";
import { useGetBookerData } from "./useGetBookerData";
import {
  BookerContext,
  BookerContextValues,
  type DefaultContact,
  type PaymentRequestAppliedDiscount,
  type PaymentRequestLineItem,
  type UserOperator,
} from "./BookerContext";
import { BookerPosition } from "widgets/booker/App";
import { CartSnapshot } from "../CartContextProvider";
import { CATALOG_GRID_MIN_PRODUCTS } from "shared/merchCatalog";
import type { MembershipCheckoutIntent } from "shared/utils/membershipIntent";

type BookerContextProviderProps = PropsWithChildren & {
  onCloseBookerModal: () => void;
  bookerId: string;
  isEmbedded: boolean;
  language: i18nLanguage;
  hasOpenedOnce: boolean;
  defaultBooker?: BookerByIdQuery["bookerById"];
  /** When true, BookerById is refetched on open so fields like brandingLogo stay in sync with storefront settings. */
  bookerModalOpen: boolean;
  bookerPosition: BookerPosition;
  broadcastId?: string;
  broadcastResourceId?: string;
  defaultContact?: DefaultContact;
  userOperators?: UserOperator[];
  paymentRequestId?: string;
  paymentRequestVersion?: number;
  paymentRequestGrandTotal?: number;
  paymentRequestSubtotal?: number;
  paymentRequestTaxAmount?: number;
  paymentRequestTaxLines?: {
    name: string;
    ratePercent?: number | null;
    amount: number;
  }[];
  paymentRequestCustomerFee?: number | null;
  paymentRequestAdditionalLineItems?: PaymentRequestLineItem[];
  paymentRequestAppliedDiscount?: PaymentRequestAppliedDiscount;
  initialCartSnapshot?: CartSnapshot;
  readOnlySelection?: boolean;
  disableResetOnOpen?: boolean;
  invoiceAdminMode?: boolean;
  isStorefrontOwner?: boolean;
  storefrontId?: string;
  organizationId?: string;
  membershipIntent?: MembershipCheckoutIntent;
};

export const BookerContextProvider: React.FC<BookerContextProviderProps> = ({
  onCloseBookerModal,
  bookerId,
  children,
  isEmbedded,
  language,
  hasOpenedOnce,
  defaultBooker,
  bookerModalOpen,
  bookerPosition,
  broadcastId,
  broadcastResourceId,
  defaultContact,
  userOperators,
  paymentRequestId,
  paymentRequestVersion,
  paymentRequestGrandTotal,
  paymentRequestSubtotal,
  paymentRequestTaxAmount,
  paymentRequestTaxLines,
  paymentRequestCustomerFee,
  paymentRequestAdditionalLineItems,
  paymentRequestAppliedDiscount,
  initialCartSnapshot,
  readOnlySelection,
  disableResetOnOpen,
  invoiceAdminMode,
  isStorefrontOwner,
  storefrontId,
  organizationId,
  membershipIntent,
}: BookerContextProviderProps) => {
  const {
    error,
    loading,
    owner,
    product,
    booker,
    stripeTestMode,
    storefrontIsPaired,
  } = useGetBookerData(bookerId, hasOpenedOnce, isEmbedded, bookerModalOpen);

  const contextValue: BookerContextValues = useMemo(() => {
    const resolvedBooker = defaultBooker ?? booker ?? null;
    const products = (resolvedBooker?.bundles ?? [])
      .map(bundle => bundle?.product as Product | null | undefined)
      .filter((p): p is Product => !!p);
    const isCatalog =
      products.length > 1 &&
      products.every(p => p.type === EnumProductType.Merch);
    const catalogLayout = !isCatalog
      ? null
      : products.length >= CATALOG_GRID_MIN_PRODUCTS
        ? "grid"
        : "list";
    return {
      booker: resolvedBooker,
      product: resolvedBooker
        ? ((resolvedBooker.bundles[0]?.product as Product) ?? product)
        : product,
      products,
      isCatalog,
      catalogLayout,
      owner,
      loading,
      error,
      onCloseBookerModal,
      language,
      isEmbedded,
      bookerPosition,
      broadcastId,
      broadcastResourceId,
      defaultContact,
      userOperators,
      stripeTestMode,
      storefrontIsPaired,
      paymentRequestId,
      paymentRequestVersion,
      paymentRequestGrandTotal,
      paymentRequestSubtotal,
      paymentRequestTaxAmount,
      paymentRequestTaxLines,
      paymentRequestCustomerFee,
      paymentRequestAdditionalLineItems,
      paymentRequestAppliedDiscount,
      initialCartSnapshot,
      readOnlySelection,
      disableResetOnOpen,
      invoiceAdminMode,
      isStorefrontOwner,
      storefrontId,
      organizationId,
      membershipIntent,
    };
  }, [
      booker,
      product,
      owner,
      loading,
      error,
      onCloseBookerModal,
      language,
      isEmbedded,
      defaultBooker,
      bookerPosition,
      broadcastId,
      broadcastResourceId,
      defaultContact,
      userOperators,
      stripeTestMode,
      storefrontIsPaired,
      paymentRequestId,
      paymentRequestVersion,
      paymentRequestGrandTotal,
      paymentRequestSubtotal,
      paymentRequestTaxAmount,
      paymentRequestTaxLines,
      paymentRequestCustomerFee,
      paymentRequestAdditionalLineItems,
      paymentRequestAppliedDiscount,
      initialCartSnapshot,
      readOnlySelection,
      disableResetOnOpen,
      invoiceAdminMode,
      isStorefrontOwner,
      storefrontId,
      organizationId,
      membershipIntent,
    ],
  );

  return (
    <BookerContext.Provider value={contextValue}>
      {children}
    </BookerContext.Provider>
  );
};
